/* eslint-disable max-lines -- Complex P2P stack manager with monitoring, reconnection, and lifecycle management */
/* eslint-disable no-magic-numbers -- Timeout/interval values and connection limits are more readable inline */
// src/services/p2p/p2p-stack-manager.impl.ts

import { multiaddr } from '@multiformats/multiaddr';
import { peerIdFromString } from '@libp2p/peer-id';

import type { HeliaNode } from "@/types/poc/helia";
import type { LibP2pWithServices, LibP2pNodeCallbacks } from "@/types/poc/libp2p";
import type { HeliaService } from "@/services/abstract/poc/helia.service.interface";
import type { Libp2pService } from "@/services/abstract/poc/libp2p.service.interface";
import type { MessagingService } from "@/services/abstract/poc/messaging.service.interface";
import { SIX_ID_ANNOUNCE_TOPIC, MESH_HEARTBEAT_TOPIC, SIX_TOPIC } from "@/config/networkConstants";
import { P2P_CONFIG } from "@/config/p2pConfig";
import { type P2PStackInstance, P2PStackManager, type P2PStackOptions, type InitializeStackResult, type NodeMetadata } from "@/services/abstract/poc/p2p.stack-manager.interface";
import type { Logger } from "@/types/Logger";
import type { LoggerService } from "@/services/abstract/LoggerService";

/* -------------------------------------------------------------------------- */
/*  Constants                                                                 */
/* -------------------------------------------------------------------------- */

const MESH_HEARTBEAT_INTERVAL_MS = 10000;

// Track which six is responsible for heartbeat (only one per browser)
let heartbeatOwnerSixId: string | null = null;

/* -------------------------------------------------------------------------- */
/*  Pure Helper Functions                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Creates initial node metadata with empty state
 */
const createInitialNodeMetadata = (sixId: string, bootstrapNodes: string[]): NodeMetadata => ({
  status: {
    relayConnected: false,
    subscribedTopics: [],
    subscriberCounts: new Map(),
    connections: [],
    peerId: '',
    connectionStats: {
      activeConnections: {
        direct: 0,
        relay: 0,
        total: 0
      },
      lifetimeStats: {
        directConnections: 0,
        relayConnections: 0,
        directDisconnections: 0,
        relayDisconnections: 0,
        totalConnections: 0,
        totalDisconnections: 0
      },
      uniquePeers: {
        direct: new Set(),
        relay: new Set(),
        total: new Set()
      }
    }
  },
  config: {
    groupId: sixId,
    relayAddresses: bootstrapNodes
  },
  timers: {
    healthCheckInterval: null,
    updateInterval: null,
    reconnectInterval: null,
    topicListBroadcastInterval: null,
    sixIdAnnounceTimer: null
  },
  reconnectState: {
    attempts: 0,
    maxAttempts: 50,
    relayPeerId: null
  },
  peerTracking: {
    connectionTimers: new Map(),
    blockedPeers: new Set(),
    reconnectionIntervals: new Map(),
    connectionAttempts: new Map(),
    lastConnectionAttempt: new Map()
  }
});

const createEmptyStackInstance = (sixId: string): P2PStackInstance => ({
  sixId,
  libp2pNode: null,
  heliaNode: null,
  subscriptions: null,
  isInitialized: false,
  fileListBroadcastInterval: null,
  subscriberCheckTimer: null,
  nodeMetadata: null,
  meshHeartbeatInterval: null,
});

const findStackInstance = (
  stacks: P2PStackInstance[],
  sixId: string
): P2PStackInstance | undefined =>
  stacks.find(stack => stack.sixId === sixId);

const addStackInstance = (
  stacks: P2PStackInstance[],
  newStack: P2PStackInstance
): P2PStackInstance[] =>
  [...stacks, newStack];

const removeStackInstance = (
  stacks: P2PStackInstance[],
  sixId: string
): P2PStackInstance[] =>
  stacks.filter(stack => stack.sixId !== sixId);

const updateStackInstance = (
  stack: P2PStackInstance,
  updates: Partial<P2PStackInstance>
): P2PStackInstance => ({
  ...stack,
  ...updates,
});

const shouldInitializeLibp2p = (options: P2PStackOptions): boolean =>
  options.autoConnectLibp2p ?? true;

const shouldInitializeHelia = (
  options: P2PStackOptions,
  stack: P2PStackInstance
): boolean =>
  (options.autoInitializeHelia ?? true) && stack.libp2pNode !== null;

const shouldInitializeMessaging = (
  options: P2PStackOptions,
  stack: P2PStackInstance
): boolean =>
  (options.autoInitializeMessaging ?? true) &&
  stack.libp2pNode !== null &&
  stack.heliaNode !== null;

const getMessagingTopics = (options: P2PStackOptions, includeHeartbeat: boolean): string[] => {
  const topics = [
    // PEER_DISCOVERY_TOPIC(options.sixId),
    // SIX_ID_ANNOUNCE_TOPIC,
    // Subscribe to HAP_FILE_ANNOUNCE to form mesh with HAP (browser publishes here, HAP receives)
    // Messages on this topic are ignored by the browser - subscription is only for mesh formation
    // HAP_FILE_ANNOUNCE_TOPIC(options.sixId),
    SIX_TOPIC(options.sixId)
  ];

  // Only include heartbeat topic for the designated heartbeat owner
  if (includeHeartbeat) {
    topics.push(MESH_HEARTBEAT_TOPIC);
  }

  return topics;
};

const createSuccessResult = (
  sixId: string,
  peerId: string
): InitializeStackResult => ({
  sixId,
  peerId,
  isSuccessful: true,
});

/* -------------------------------------------------------------------------- */
/*  Implementation                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Manages multiple P2P stacks, one per six.
 *
 * Each six gets its own isolated LibP2P node, Helia node, and messaging subscriptions.
 * Stacks are stored in an array and managed using pure functional operations.
 */
export class P2PStackManagerImpl extends P2PStackManager {
  private readonly log: Logger;
  #stackInstances: P2PStackInstance[] = [];

  constructor(
    loggerService: LoggerService,
    private readonly libp2pService: Libp2pService,
    private readonly heliaService: HeliaService,
    private readonly messagingService: MessagingService
  ) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:p2p:stack-manager' });
  }

  /* ------------------------------------------------------------------------ */
  /*  Stack Initialization                                                   */
  /* ------------------------------------------------------------------------ */

  async initializeStack(options: P2PStackOptions): Promise<InitializeStackResult> {
    const { sixId } = options;

    // Early return if already initialized
    const existingResult = this.checkExistingStack(sixId);
    if (existingResult !== null) {
      return existingResult;
    }

    this.log.info(`Initializing stack for six '${sixId}'`, options);

    const stackInstance = createEmptyStackInstance(sixId);
    stackInstance.fileListTopic = options.fileListTopic;
    stackInstance.onStatusUpdate = options.onStatusUpdate;
    stackInstance.onConnectionUpdate = options.onConnectionUpdate;

    try {
      const initializedStack = await this.buildStackLayers(stackInstance, options);
      this.#stackInstances = addStackInstance(this.#stackInstances, initializedStack);

      this.scheduleTopicListPublication(sixId, initializedStack);

      this.log.info(`Successfully initialized stack for six '${sixId}'`);
      return createSuccessResult(sixId, initializedStack.libp2pNode?.peerId.toString() ?? '');

    } catch (error) {
      await this.handleInitializationError(sixId, stackInstance, error);
      throw error; // Re-throw after cleanup
    }
  }

  /* ------------------------------------------------------------------------ */
  /*  Private Initialization Helpers                                         */
  /* ------------------------------------------------------------------------ */

  private checkExistingStack(sixId: string): InitializeStackResult | null {
    const existingStack = findStackInstance(this.#stackInstances, sixId);
    if (existingStack?.isInitialized === true) {
      this.log.info(`Stack for six '${sixId}' is already initialized`);
      return createSuccessResult(sixId, existingStack.libp2pNode?.peerId.toString() ?? '');
    }
    return null;
  }

  private async buildStackLayers(
    stack: P2PStackInstance,
    options: P2PStackOptions
  ): Promise<P2PStackInstance> {
    let currentStack = stack;

    // Layer 1: LibP2P
    if (shouldInitializeLibp2p(options)) {
      currentStack = await this.initializeLibp2pLayer(currentStack, options);
    }

    // Layer 2: Helia
    if (shouldInitializeHelia(options, currentStack)) {
      currentStack = await this.initializeHeliaLayer(currentStack, options.sixId);
    }

    // Layer 3: Messaging
    if (shouldInitializeMessaging(options, currentStack)) {
      currentStack = this.initializeMessagingLayer(currentStack, options);
    }

    return currentStack;
  }

  private async initializeLibp2pLayer(
    stack: P2PStackInstance,
    options: P2PStackOptions
  ): Promise<P2PStackInstance> {
    // Initialize node metadata first with bootstrap nodes as relay addresses
    const nodeMetadata = createInitialNodeMetadata(options.sixId, options.bootstrapNodes);

    // Assign metadata to stack BEFORE creating callbacks so callbacks can access it
    stack.nodeMetadata = nodeMetadata;

    // Store sixId for lookup in callbacks
    const { sixId } = options;

    // Create callbacks that look up the current stack instance from the manager
    const callbacks: LibP2pNodeCallbacks = {
      onStatusChange: (status) => {
        const currentStack = findStackInstance(this.#stackInstances, sixId);
        if (currentStack?.nodeMetadata !== null && currentStack !== undefined) {
          currentStack.nodeMetadata.status = status;
        }
      },
      onConnectionOpen: (info) => {
        const currentStack = findStackInstance(this.#stackInstances, sixId);
        if (currentStack?.nodeMetadata == null) return;

        const { connectionStats } = currentStack.nodeMetadata.status;

        // Update lifetime stats
        connectionStats.lifetimeStats.totalConnections++;
        if (info.isDirect) {
          connectionStats.lifetimeStats.directConnections++;
          connectionStats.uniquePeers.direct.add(info.peerId);
        } else {
          connectionStats.lifetimeStats.relayConnections++;
          connectionStats.uniquePeers.relay.add(info.peerId);
        }
        connectionStats.uniquePeers.total.add(info.peerId);

        // Update connections list and notify Redux immediately
        this.updateConnectionsList(currentStack);
      },
      onConnectionClose: (info) => {
        const currentStack = findStackInstance(this.#stackInstances, sixId);
        if (currentStack?.nodeMetadata == null) return;

        const { connectionStats } = currentStack.nodeMetadata.status;

        // Update lifetime stats
        connectionStats.lifetimeStats.totalDisconnections++;
        if (info.isDirect) {
          connectionStats.lifetimeStats.directDisconnections++;
        } else {
          connectionStats.lifetimeStats.relayDisconnections++;
        }

        // Check if this is a relay server disconnection (by checking if peerId matches any relay address)
        const isRelayServer = currentStack.nodeMetadata.config.relayAddresses.some(addr =>
          addr.includes(info.peerId)
        );

        // Log relay disconnections with more detail
        if (isRelayServer) {
          this.log.warn(`⚠️ RELAY CONNECTION CLOSED`);
          this.log.warn(`   Relay: ${info.peerId.substring(0, 20)}...`);
          this.log.warn(`   Direction: ${info.direction}`);
          this.log.warn(`   Address: ${info.address}`);

          // Check if we still have any relay connections
          if (currentStack.libp2pNode != null) {
            const connections = currentStack.libp2pNode.getConnections();
            const relayConns = connections.filter(c => {
              const addr = c.remoteAddr.toString();
              return addr.includes('/p2p-circuit') || addr.includes('/wss/');
            });

            this.log.warn(`   Remaining relay connections: ${relayConns.length}`);

            if (relayConns.length === 0) {
              this.log.error(`🚨 ALL RELAY CONNECTIONS LOST - triggering reconnection`);
            }
          }
        }

        // Update connections list and notify Redux immediately
        this.updateConnectionsList(currentStack);

        // If we lost connection to relay server, start reconnection attempts
        if (isRelayServer) {
          this.log.warn(`Lost connection to relay server: ${info.peerId.substring(0, 20)}...`);
          currentStack.nodeMetadata.status.relayConnected = false;
          // this.startReconnectAttempts(currentStack);
        }
      },
      onPeerConnect: (peerId, connections) => {
        const currentStack = findStackInstance(this.#stackInstances, sixId);
        if (currentStack?.nodeMetadata == null) return;

        this.log.info(`🔗 PEER CONNECTED: ${peerId}`);

        // Stop reconnection attempts if this peer was being reconnected
        const intervalId = currentStack.nodeMetadata.peerTracking.reconnectionIntervals.get(peerId);
        if (intervalId !== undefined) {
          clearInterval(intervalId);
          currentStack.nodeMetadata.peerTracking.reconnectionIntervals.delete(peerId);
          currentStack.nodeMetadata.peerTracking.connectionAttempts.delete(peerId);
        }

        const existingTimer = currentStack.nodeMetadata.timers.sixIdAnnounceTimer;
        if (existingTimer !== null) {
          clearTimeout(existingTimer);
        }

        // Republish after 5 seconds
        // Update connections list and notify Redux immediately
        this.updateConnectionsList(currentStack);
      },
      onPeerDisconnect: (peerId) => {
        const currentStack = findStackInstance(this.#stackInstances, sixId);

        this.log.info(`🔔 [Stack Manager] onPeerDisconnect callback triggered for peer: ${peerId}`);

        if (currentStack?.nodeMetadata == null) {
          this.log.warn(`⚠️ nodeMetadata is null or stack not found, cannot process disconnect for ${peerId}`);
          return;
        }

        this.log.info(`❌ [Stack Manager] PEER DISCONNECTED: ${peerId}`);

        // Log current connection state
        if (currentStack.libp2pNode != null) {
          const allConnections = currentStack.libp2pNode.getConnections();
          const directConns = allConnections.filter(c => c.remoteAddr.toString().includes('/webrtc/'));
          const relayConns = allConnections.filter(c => {
            const addr = c.remoteAddr.toString();
            return addr.includes('/p2p-circuit') || addr.includes('/wss/');
          });

          this.log.info(`   Connection state: ${directConns.length} direct, ${relayConns.length} relay, ${allConnections.length} total`);
        }

        // Clean up timer when peer disconnects
        const timer = currentStack.nodeMetadata.peerTracking.connectionTimers.get(peerId);
        if (timer !== undefined) {
          clearTimeout(timer);
          currentStack.nodeMetadata.peerTracking.connectionTimers.delete(peerId);
        }

        // Check if this is a relay server disconnection
        this.log.debug(`Checking if ${peerId} is relay server...`);
        this.log.debug(`Relay addresses: ${JSON.stringify(currentStack.nodeMetadata.config.relayAddresses)}`);
        const isRelayServer = currentStack.nodeMetadata.config.relayAddresses.some(addr => {
          const matches = addr.includes(peerId);
          this.log.debug(`  ${addr} includes ${peerId}? ${matches}`);
          return matches;
        });
        this.log.debug(`Is relay server: ${isRelayServer}`);

        if (isRelayServer) {
          currentStack.nodeMetadata.status.relayConnected = false;
          this.log.warn(`🔴 Lost connection to relay peer: ${peerId.substring(0, 20)}...`);
          this.log.warn(`   This was the relay server - will attempt reconnection`);
          // this.startReconnectAttempts(currentStack);
        } else {
          // For non-relay peers, attempt to reconnect
          this.log.info(`📡 Will attempt to reconnect to peer: ${peerId.substring(0, 20)}...`);
          // this.startPeerReconnection(currentStack, peer/Id);
        }

        // Update connections list and notify Redux immediately
        this.updateConnectionsList(currentStack);
      },
      onSubscriptionChange: (_peerId, _subscriptions) => {
        const currentStack = findStackInstance(this.#stackInstances, sixId);
        if (currentStack?.nodeMetadata == null || currentStack.libp2pNode == null) return;

        // Update subscriber counts
        const counts = new Map<string, number>();
        for (const topic of currentStack.nodeMetadata.status.subscribedTopics) {
          const subscribers = currentStack.libp2pNode.services.pubsub.getSubscribers(topic);
          counts.set(topic, subscribers.length);
        }
        currentStack.nodeMetadata.status.subscriberCounts = counts;
      }
    };

    const libp2pNode = await this.libp2pService.createNode({
      swarmKey: options.swarmKey,
      sixId: options.sixId,
      fileListTopic: options.fileListTopic,
      fileTransferTopic: options.fileTransferTopic,
      bootstrapNodes: options.bootstrapNodes,
      callbacks,
      turnConfig: options.turnConfig
    });

    // Update nodeMetadata with peer ID
    nodeMetadata.status.peerId = libp2pNode.peerId.toString();
    nodeMetadata.status.subscribedTopics = [];

    // Start periodic status updates and health checks
    this.startNodeMonitoring(stack, libp2pNode);

    return updateStackInstance(stack, { libp2pNode, nodeMetadata });
  }

  private async initializeHeliaLayer(
    stack: P2PStackInstance,
    sixId: string
  ): Promise<P2PStackInstance> {
    if (stack.libp2pNode === null) {
      throw new Error('LibP2P node must be initialized before Helia');
    }

    this.log.info(`   🔄 Initializing Helia node for six '${sixId}'...`);
    const heliaNode = await this.heliaService.createNode(stack.libp2pNode);
    this.log.info(`   ✅ Helia node created:`, {
      peerId: stack.libp2pNode.peerId.toString(),
    });

    return updateStackInstance(stack, { heliaNode });
  }

  private initializeMessagingLayer(
    stack: P2PStackInstance,
    options: P2PStackOptions
  ): P2PStackInstance {
    if (stack.libp2pNode === null || stack.heliaNode === null) {
      throw new Error('LibP2P and Helia must be initialized before messaging');
    }

    // Determine if this six should be the heartbeat owner (first one wins)
    const isHeartbeatOwner = heartbeatOwnerSixId === null;
    if (isHeartbeatOwner) {
      heartbeatOwnerSixId = options.sixId;
      this.log.info(`💓 Six '${options.sixId}' is now the MESH HEARTBEAT owner`);
      this.log.info(`💓 Heartbeat owner peer ID: ${stack.libp2pNode.peerId.toString()}`);
    }

    const messageCallback = options.onMessage ?? (() => { /* empty */ });
    const topics = getMessagingTopics(options, isHeartbeatOwner);

    const subscriptionCleanup = this.messagingService.setupSubscriptions(
      stack.libp2pNode,
      stack.heliaNode,
      messageCallback,
      topics
    );

    // Update subscribedTopics to include all messaging topics
    if (stack.nodeMetadata !== null) {
      stack.nodeMetadata.status.subscribedTopics = [...topics];
    }

    // Start mesh heartbeat interval if this six is the owner
    let meshHeartbeatInterval: ReturnType<typeof setInterval> | null = null;
    if (isHeartbeatOwner && stack.libp2pNode !== null) {
      meshHeartbeatInterval = this.startMeshHeartbeat(stack, options.sixId);
    }

    return updateStackInstance(stack, {
      subscriptions: { cleanup: subscriptionCleanup },
      isInitialized: true,
      meshHeartbeatInterval,
    });
  }

  private scheduleTopicListPublication(sixId: string, stack: P2PStackInstance): void {
    if (stack.nodeMetadata === null) return;

    // Publish topic list immediately after a short delay for relay connection
    setTimeout(() => {
      void this.publishTopicListSafely(sixId, stack);
    }, 5000);
  }

  /**
   * Start mesh heartbeat for diagnostic purposes.
   * Publishes heartbeat every 10 seconds with mesh state.
   */
  private startMeshHeartbeat(stack: P2PStackInstance, sixId: string): ReturnType<typeof setInterval> {
    let seq = 0;

    const publishHeartbeat = (): void => {
      if (stack.libp2pNode === null) return;

      seq++;
      const pubsub = stack.libp2pNode.services.pubsub;

      // Get mesh state from GossipSub (if available)
      const gossipPubsub = pubsub as unknown as {
        mesh?: Map<string, Set<string>>;
      };

      let meshTotal = 0;
      const meshByTopic: Record<string, number> = {};

      if (gossipPubsub.mesh !== undefined) {
        for (const [topic, peers] of gossipPubsub.mesh.entries()) {
          const peerCount = peers.size;
          meshTotal += peerCount;
          if (peerCount > 0) {
            meshByTopic[topic] = peerCount;
          }
        }
      }

      const heartbeat = {
        type: 'mesh-heartbeat',
        source: 'browser',
        seq,
        sixId,
        timestamp: Date.now(),
        sender: stack.libp2pNode.peerId.toString(),
        connections: stack.libp2pNode.getConnections().length,
        meshTotal,
        meshByTopic
      };

      // Get subscribers to the heartbeat topic
      const heartbeatSubscribers = pubsub.getSubscribers(MESH_HEARTBEAT_TOPIC);

      this.log.debug('💓 MESH HEARTBEAT [Browser→HAP]', {
        seq,
        sixId,
        meshTotal,
        connections: heartbeat.connections,
        meshTopics: Object.keys(meshByTopic).length,
        heartbeatSubscribers: heartbeatSubscribers.length
      });

      if (heartbeatSubscribers.length === 0) {
        this.log.debug('⚠️  NO SUBSCRIBERS to MESH_HEARTBEAT topic - no one will receive this message!');
      } else {
        this.log.debug(`   📬 Subscribers: ${heartbeatSubscribers.map(p => p.toString().substring(0, 16) + '...').join(', ')}`);
      }

      if (meshTotal === 0) {
        this.log.debug('⚠️  BROWSER MESH IS EMPTY - messages may not flow!');
      }

      void (async() => {
        try {
          const data = JSON.stringify(heartbeat);
          if (stack.libp2pNode !== null) {
            await this.libp2pService.publishToTopic(stack.libp2pNode, MESH_HEARTBEAT_TOPIC, data);
          }
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          this.log.error('Failed to publish mesh heartbeat', {
            error: errorMessage
          });
        }
      })();
    };

    // Publish first heartbeat after a short delay for connections to establish
    setTimeout(publishHeartbeat, 5000);

    // Then publish every 10 seconds
    return setInterval(publishHeartbeat, MESH_HEARTBEAT_INTERVAL_MS);
  }

  /* ------------------------------------------------------------------------ */
  /*  Node Monitoring & State Management Helpers                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Update the connections list in node metadata based on current libp2p connections
   */
  private updateConnectionsList(stack: P2PStackInstance): void {
    if (stack.nodeMetadata === null || stack.libp2pNode === null) return;

    const connections = stack.libp2pNode.getConnections();
    let directCount = 0;
    let relayCount = 0;

    const connectionDetails = connections.map(conn => {
      const addr = conn.remoteAddr.toString();
      const isDirect = addr.includes('/webrtc/') && !addr.includes('/p2p-circuit');
      const isRelay = addr.includes('/p2p-circuit') || addr.includes('/ws/') || addr.includes('/wss/');

      if (isDirect) directCount++;
      if (isRelay) relayCount++;

      return {
        peerId: conn.remotePeer.toString(),
        address: addr,
        isDirect,
        isRelay,
        direction: conn.direction
      };
    });

    stack.nodeMetadata.status.connections = connectionDetails;

    // Update active connection counts
    stack.nodeMetadata.status.connectionStats.activeConnections = {
      direct: directCount,
      relay: relayCount,
      total: connections.length
    };

    // Notify status update callback with connection details for Redux
    if (stack.onConnectionUpdate !== undefined) {
      stack.onConnectionUpdate(stack.sixId, connectionDetails);
    }

    // Check if connected to relay
    const nodeMetadata = stack.nodeMetadata;
    const relayConnection = connections.find(conn => {
      const addr = conn.remoteAddr.toString();
      return nodeMetadata.config.relayAddresses.some(relayAddr => {
        const relayPeerId = relayAddr.split('/p2p/')[1];
        return addr.includes(relayPeerId);
      });
    });

    const wasConnected = stack.nodeMetadata.status.relayConnected;

    if (relayConnection === undefined) {
      if (wasConnected) {
        this.log.warn(`⚠️ Relay connection lost for ${stack.sixId}`);
      }
      stack.nodeMetadata.status.relayConnected = false;

      // Start reconnection if not already trying
      if (stack.nodeMetadata.timers.reconnectInterval === null) {
        this.log.info(`🔄 Starting relay reconnection from updateConnectionsList`);
        this.startReconnectAttempts(stack);
      }
    } else {
      if (!wasConnected) {
        this.log.info(`✅ RELAY CONNECTION ESTABLISHED for ${stack.sixId}`);
        // Reset reconnection attempts counter when successfully reconnected
        stack.nodeMetadata.reconnectState.attempts = 0;
      }
      stack.nodeMetadata.status.relayConnected = true;

      // Extract relay peer ID
      const relayAddr = stack.nodeMetadata.config.relayAddresses[0];
      const relayPeerId = relayAddr.split('/p2p/')[1];
      stack.nodeMetadata.reconnectState.relayPeerId = relayPeerId;

      // Stop reconnect attempts if we just connected
      this.stopReconnectAttempts(stack);
    }
  }

  /**
   * Start monitoring intervals for the node (subscriber counts and health checks)
   * Connection updates are now handled via events (onConnectionOpen/Close, onPeerConnect/Disconnect)
   */
  private startNodeMonitoring(stack: P2PStackInstance, node: LibP2pWithServices): void {
    if (stack.nodeMetadata === null) return;

    const peerId = node.peerId.toString();

    // Periodic subscriber count updates and relay health check
    const updateInterval = setInterval(() => {
      // Update subscriber counts
      if (stack.nodeMetadata?.status.subscribedTopics !== undefined && stack.libp2pNode !== null) {
        const counts = new Map<string, number>();
        for (const topic of stack.nodeMetadata.status.subscribedTopics) {
          const subscribers = stack.libp2pNode.services.pubsub.getSubscribers(topic);
          counts.set(topic, subscribers.length);
        }
        stack.nodeMetadata.status.subscriberCounts = counts;

        // Call status update callback with subscriber counts
        if (stack.onStatusUpdate !== undefined) {
          const subscriberCountsObject: Record<string, number> = {};
          counts.forEach((count, topic) => {
            subscriberCountsObject[topic] = count;
          });
          stack.onStatusUpdate(stack.sixId, subscriberCountsObject);
        }
      }

      // Health check: ensure we're connected to relay
      if (stack.nodeMetadata?.status.relayConnected === false && stack.nodeMetadata.timers.reconnectInterval === null) {
        // this.startReconnectAttempts(stack);
      }
    }, 3000);

    stack.nodeMetadata.timers.updateInterval = updateInterval;

    // WebRTC connection health check and logging
    const healthCheckInterval = setInterval(() => {
      if (stack.libp2pNode === null) return;

      const allConnections = stack.libp2pNode.getConnections();
      const webrtcConnections = allConnections.filter(conn => {
        const addr = conn.remoteAddr.toString();
        return addr.includes('/webrtc/') && !addr.includes('/p2p-circuit');
      });

      this.log.info(`🔍 WebRTC Health Check [${stack.sixId}]:`);
      this.log.info(`   Total connections: ${allConnections.length}`);
      this.log.info(`   WebRTC direct: ${webrtcConnections.length}`);
      this.log.info(`   Circuit relay: ${allConnections.length - webrtcConnections.length}`);
    }, 10000);

    stack.nodeMetadata.timers.healthCheckInterval = healthCheckInterval;

    this.log.info(`Started monitoring for node: ${peerId.substring(0, 20)}...`);
  }

  /**
   * Start reconnection attempts for a disconnected relay
   */
  private startReconnectAttempts(stack: P2PStackInstance): void {
    this.log.info(`Attempting to start relay reconnection attempts for ${stack.sixId}...`, stack.nodeMetadata, stack.libp2pNode, stack);
    if (stack.nodeMetadata === null || stack.libp2pNode === null) return;
    this.log.info(`Relay reconnection attempts for ${stack.sixId} proceeding...`);
    // Don't start if already reconnecting
    if (stack.nodeMetadata.timers.reconnectInterval !== null) {
      this.log.debug(`Already reconnecting to relay for ${stack.sixId}, skipping`);
      return;
    }

    stack.nodeMetadata.reconnectState.attempts = 0;
    this.log.info(`🔄 Starting relay reconnection attempts for ${stack.sixId}...`);

    const reconnectInterval = setInterval(() => {
      void (async() => {
        if (stack.nodeMetadata === null || stack.libp2pNode === null) {
          this.stopReconnectAttempts(stack);
          return;
        }

        stack.nodeMetadata.reconnectState.attempts++;

        if (stack.nodeMetadata.reconnectState.attempts > stack.nodeMetadata.reconnectState.maxAttempts) {
          this.log.error(`❌ Max reconnection attempts (${stack.nodeMetadata.reconnectState.maxAttempts}) reached for ${stack.sixId}`);
          this.stopReconnectAttempts(stack);
          return;
        }

        // Check if we're already connected
        if (stack.nodeMetadata.status.relayConnected) {
          this.log.info(`✅ Relay already reconnected for ${stack.sixId}, stopping attempts`);
          this.stopReconnectAttempts(stack);
          return;
        }

        this.log.info(`🔄 Relay reconnect attempt ${stack.nodeMetadata.reconnectState.attempts}/${stack.nodeMetadata.reconnectState.maxAttempts} for ${stack.sixId}...`);

        // Try to dial each relay address
        this.log.info(`   Relay addresses to try: ${JSON.stringify(stack.nodeMetadata.config.relayAddresses)}`);
        for (const addr of stack.nodeMetadata.config.relayAddresses) {
          try {
            this.log.info(`   Dialing relay: ${addr}`);
            await stack.libp2pNode.dial(multiaddr(addr));
            this.log.info(`✅ Successfully reconnected to relay: ${addr}`);
            break;
          } catch (err) {
            const errMsg = err instanceof Error ? err.message : String(err);
            this.log.info(`   ⚠️ Failed to reconnect to ${addr}: ${errMsg}`);
          }
        }
      })();
    }, 5000); // Try every 5 seconds

    stack.nodeMetadata.timers.reconnectInterval = reconnectInterval;
  }

  /**
   * Stop reconnection attempts for a stack
   */
  private stopReconnectAttempts(stack: P2PStackInstance): void {
    if (stack.nodeMetadata === null) return;

    if (stack.nodeMetadata.timers.reconnectInterval !== null) {
      this.log.info(`🛑 Stopping relay reconnection attempts for ${stack.sixId}`);
      clearInterval(stack.nodeMetadata.timers.reconnectInterval);
      stack.nodeMetadata.timers.reconnectInterval = null;
      stack.nodeMetadata.reconnectState.attempts = 0;
    }
  }

  /**
   * Start reconnection attempts for a specific peer with exponential backoff
   */
  private startPeerReconnection(stack: P2PStackInstance, peerId: string): void {
    if (stack.nodeMetadata === null || stack.libp2pNode === null) return;

    // Don't reconnect to relay servers (handled separately)
    const isRelayServer = stack.nodeMetadata.config.relayAddresses.some(addr =>
      addr.includes(peerId)
    );
    if (isRelayServer) return;

    // Check if already reconnecting to this peer
    const existingInterval = stack.nodeMetadata.peerTracking.reconnectionIntervals.get(peerId);
    if (existingInterval !== undefined) {
      this.log.debug(`Already reconnecting to peer: ${peerId.substring(0, 20)}...`);
      return;
    }

    // Initialize connection attempts counter
    const currentAttempts = stack.nodeMetadata.peerTracking.connectionAttempts.get(peerId) ?? 0;
    stack.nodeMetadata.peerTracking.connectionAttempts.set(peerId, currentAttempts);

    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_DELAY_MS = 2000;
    const MAX_DELAY_MS = 30000;

    const attemptReconnection = (): void => {
      void (async() => {
        if (stack.nodeMetadata === null || stack.libp2pNode === null) {
          this.stopPeerReconnection(stack, peerId);
          return;
        }

        const attempts = stack.nodeMetadata.peerTracking.connectionAttempts.get(peerId) ?? 0;

        // Check if we've exceeded max attempts
        if (attempts >= MAX_RECONNECT_ATTEMPTS) {
          this.log.warn(`Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached for peer: ${peerId.substring(0, 20)}...`);
          this.stopPeerReconnection(stack, peerId);
          // Add to blocked peers to avoid immediate retry
          stack.nodeMetadata.peerTracking.blockedPeers.add(peerId);
          // Remove from blocked after some time
          setTimeout(() => {
            stack.nodeMetadata?.peerTracking.blockedPeers.delete(peerId);
          }, 60000); // Unblock after 1 minute
          return;
        }

        // Check if we're already connected
        const existingConnections = stack.libp2pNode.getConnections().filter(
          conn => conn.remotePeer.toString() === peerId
        );
        if (existingConnections.length > 0) {
          this.log.info(`✅ Already reconnected to peer: ${peerId.substring(0, 20)}...`);
          this.stopPeerReconnection(stack, peerId);
          return;
        }

        // Attempt to dial the peer
        stack.nodeMetadata.peerTracking.connectionAttempts.set(peerId, attempts + 1);
        stack.nodeMetadata.peerTracking.lastConnectionAttempt.set(peerId, Date.now());

        this.log.info(`🔄 Reconnection attempt ${attempts + 1}/${MAX_RECONNECT_ATTEMPTS} for peer: ${peerId.substring(0, 20)}...`);

        try {
          const peerIdObj = peerIdFromString(peerId);
          await stack.libp2pNode.dial(peerIdObj);
          this.log.info(`✅ Successfully reconnected to peer: ${peerId.substring(0, 20)}...`);
          this.stopPeerReconnection(stack, peerId);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          this.log.debug(`⚠️ Reconnection attempt ${attempts + 1} failed for ${peerId.substring(0, 20)}...: ${errorMessage}`);

          // Calculate exponential backoff delay
          const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempts), MAX_DELAY_MS);
          this.log.debug(`   Next attempt in ${delay}ms`);

          // Update the interval with new delay
          this.stopPeerReconnection(stack, peerId);
          const newInterval = setInterval(attemptReconnection, delay);
          stack.nodeMetadata.peerTracking.reconnectionIntervals.set(peerId, newInterval);
        }
      })();
    };

    // Start first attempt immediately
    this.log.info(`🔄 Starting reconnection for peer: ${peerId.substring(0, 20)}...`);
    const initialInterval = setInterval(attemptReconnection, BASE_DELAY_MS);
    stack.nodeMetadata.peerTracking.reconnectionIntervals.set(peerId, initialInterval);

    // Trigger first attempt now
    attemptReconnection();
  }

  /**
   * Stop reconnection attempts for a specific peer
   */
  private stopPeerReconnection(stack: P2PStackInstance, peerId: string): void {
    if (stack.nodeMetadata === null) return;

    const intervalId = stack.nodeMetadata.peerTracking.reconnectionIntervals.get(peerId);
    if (intervalId !== undefined) {
      clearInterval(intervalId);
      stack.nodeMetadata.peerTracking.reconnectionIntervals.delete(peerId);
      stack.nodeMetadata.peerTracking.connectionAttempts.delete(peerId);
      this.log.debug(`Stopped reconnection for peer: ${peerId.substring(0, 20)}...`);
    }
  }

  /* ------------------------------------------------------------------------ */
  /*  Remaining Helper Methods                                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Check for subscribers on the file list topic.
   * If no subscribers found, check again after 10 seconds.
   * If still no subscribers, disconnect and trigger reconnection.
   */
  private checkFileListSubscribers(stackInstance: P2PStackInstance): void {
    if (stackInstance.libp2pNode === null || stackInstance.fileListTopic === undefined) {
      return;
    }

    const checkSubscribers = (isRetry = false): void => {
      if (stackInstance.libp2pNode === null || stackInstance.fileListTopic === undefined) {
        return;
      }

      const subscribers = stackInstance.libp2pNode.services.pubsub.getSubscribers(stackInstance.fileListTopic);
      const subscriberCount = subscribers.length;

      this.log.info(`📊 File list topic '${stackInstance.fileListTopic}' has ${subscriberCount} subscriber(s)`);

      if (subscriberCount === 0) {
        if (isRetry) {
          // Second check failed - disconnect and let reconnection take over
          this.log.warn(`⚠️ No subscribers found on file list topic after retry. Triggering reconnection for six '${stackInstance.sixId}'`);
          void this.forceReconnection(stackInstance);
        } else {
          // First check failed - schedule retry in 10 seconds
          this.log.warn(`⚠️ No subscribers found on file list topic. Will check again in 10 seconds...`);
          const timerId = setTimeout(() => {
            checkSubscribers(true);
          }, 10000);

          // Store timer for cleanup
          this.#stackInstances = this.#stackInstances.map(stack =>
            stack.sixId === stackInstance.sixId
              ? { ...stack, subscriberCheckTimer: timerId }
              : stack
          );
        }
      } else {
        this.log.info(`✅ File list topic has ${subscriberCount} subscriber(s) - connection healthy`);
      }
    };

    // Start initial check
    checkSubscribers(false);
  }

  /**
   * Force reconnection by disconnecting from all peers and relay
   */
  private async forceReconnection(stackInstance: P2PStackInstance): Promise<void> {
    if (stackInstance.libp2pNode === null) {
      return;
    }

    this.log.info(`🔄 Forcing reconnection for six '${stackInstance.sixId}'`);

    try {
      // Get all connections
      const connections = stackInstance.libp2pNode.getConnections();
      this.log.info(`   Disconnecting from ${connections.length} peer(s)...`);

      // Close all connections
      await Promise.all(
        connections.map(async conn => {
          try {
            await conn.close();
            this.log.info(`   ✅ Disconnected from ${conn.remotePeer.toString().substring(0, 20)}...`);
          } catch (error) {
            this.log.warn(`   ⚠️ Error disconnecting from peer:`, error);
          }
        })
      );

      this.log.info(`✅ Forced disconnection complete. Reconnection service should now engage.`);
    } catch (error) {
      this.log.error(`❌ Error during forced reconnection:`, error);
    }
  }

  private async publishTopicListSafely(sixId: string, stack: P2PStackInstance): Promise<void> {
    if (stack.libp2pNode !== null && stack.nodeMetadata !== null) {
      try {
        // Get the topics that were subscribed for this six
        const topics = stack.nodeMetadata.status.subscribedTopics;
        await this.messagingService.publishTopicList(stack.libp2pNode, sixId, topics);
      } catch (error) {
        this.log.error(`Failed to publish topic list for six '${sixId}':`, error);
      }
    }
  }

  private async handleInitializationError(
    sixId: string,
    stackInstance: P2PStackInstance,
    error: unknown
  ): Promise<void> {
    const errorMessage = error instanceof Error ? error.message : String(error);
    this.log.error(`Failed to initialize stack for six '${sixId}':`, errorMessage);

    // Cleanup partial initialization
    await this.cleanupStackInstance(stackInstance);

    throw new Error(`Failed to initialize P2P stack for six '${sixId}': ${errorMessage}`);
  }

  /* ------------------------------------------------------------------------ */
  /*  Stack Shutdown                                                         */
  /* ------------------------------------------------------------------------ */

  async shutdownStack(sixId: string): Promise<void> {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);

    if (stackInstance === undefined) {
      this.log.warn(`No stack found for six '${sixId}'`);
      return;
    }

    this.log.info(`Shutting down stack for six '${sixId}'`);

    await this.cleanupStackInstance(stackInstance);

    // Remove from array
    this.#stackInstances = removeStackInstance(this.#stackInstances, sixId);

    this.log.info(`Successfully shut down stack for six '${sixId}'`);
  }

  async shutdownAllStacks(): Promise<void> {
    this.log.info(`Shutting down all ${this.#stackInstances.length} stacks`);

    await Promise.all(
      this.#stackInstances.map(async stack => { await this.cleanupStackInstance(stack); })
    );

    this.#stackInstances = [];

    this.log.info('All stacks shut down successfully');
  }

  /* ------------------------------------------------------------------------ */
  /*  Operations                                                             */
  /* ------------------------------------------------------------------------ */

  async publishMessage(sixId: string, topic: string, data: Uint8Array | string): Promise<void> {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);

    if ((stackInstance?.libp2pNode) == null) {
      throw new Error(`No initialized LibP2P node found for six '${sixId}'`);
    }

    await this.libp2pService.publishToTopic(stackInstance.libp2pNode, topic, data);
  }

  /* ------------------------------------------------------------------------ */
  /*  Getters                                                                */
  /* ------------------------------------------------------------------------ */

  getLibp2pNode(sixId: string): LibP2pWithServices | null {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);
    return stackInstance?.libp2pNode ?? null;
  }

  getHeliaNode(sixId: string): HeliaNode | null {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);
    return stackInstance?.heliaNode ?? null;
  }

  getAllSixIds(): string[] {
    return this.#stackInstances.map(stack => stack.sixId);
  }

  hasStack(sixId: string): boolean {
    return findStackInstance(this.#stackInstances, sixId) !== undefined;
  }

  getConnectionInfo(sixId: string): Array<{
    peerId: string;
    address: string;
    isDirect: boolean;
    isRelay: boolean;
    direction: 'inbound' | 'outbound';
  }> {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);

    if (stackInstance?.libp2pNode == null) {
      return [];
    }

    const connections = stackInstance.libp2pNode.getConnections();

    const connectionInfo = connections.map(conn => {
      const addr = conn.remoteAddr.toString();
      const isDirect = addr.includes('/webrtc/') && !addr.includes('/p2p-circuit');
      const isRelay = addr.includes('/p2p-circuit') || addr.includes('/ws/') || addr.includes('/wss/');

      return {
        peerId: conn.remotePeer.toString(),
        address: addr,
        isDirect,
        isRelay,
        direction: conn.direction,
      };
    });

    this.log.info(`[P2PStackManager] Connection info for Six ${sixId}:`, connectionInfo.length, 'connections');
    connectionInfo.forEach(conn => {
      let connType = 'Other';
      if (conn.isDirect) {
        connType = 'Direct';
      } else if (conn.isRelay) {
        connType = 'Relay';
      }
      this.log.info(`  - ${conn.peerId.substring(0, 20)}... (${connType})`);
    });

    return connectionInfo;
  }

  /* ------------------------------------------------------------------------ */
  /*  Connection Recovery                                                    */
  /* ------------------------------------------------------------------------ */

  reportBitswapStall(sixId: string): void {
    const stackInstance = findStackInstance(this.#stackInstances, sixId);
    if (stackInstance?.libp2pNode == null || stackInstance.nodeMetadata === null) return;

    this.log.warn(`Bitswap stall reported for six '${sixId}' — forcing reconnection`);

    void (async () => {
      try {
        await this.forceReconnection(stackInstance);

        // Re-dial the known-good bootstrap from P2P_CONFIG.
        // The catalog's bootstrapNodes may contain stale addresses
        // (e.g., a decommissioned HAP), so use the hardcoded config.
        // Note: forceReconnection closing connections also triggers
        // startReconnectAttempts via updateConnectionsList.
        if (stackInstance.libp2pNode !== null) {
          for (const addr of P2P_CONFIG.network.defaultBootstrap) {
            try {
              this.log.info(`reportBitswapStall: re-dialing bootstrap ${addr}`);
              await stackInstance.libp2pNode.dial(multiaddr(addr));
              this.log.info(`Reconnected to bootstrap after Bitswap stall: ${addr}`);
              break;
            } catch (err) {
              const errMsg = err instanceof Error ? err.message : String(err);
              this.log.warn(`Failed to re-dial bootstrap ${addr}: ${errMsg}`);
            }
          }
        }
      } catch (err) {
        this.log.error('Recovery from Bitswap stall failed:', err);
      }
    })();
  }

  /* ------------------------------------------------------------------------ */
  /*  Private Helpers                                                        */
  /* ------------------------------------------------------------------------ */

  private async cleanupStackInstance(stackInstance: P2PStackInstance): Promise<void> {
    this.log.info(`Cleaning up stack for six '${stackInstance.sixId}'`);

    // Stop file list broadcast interval
    if (stackInstance.fileListBroadcastInterval !== null) {
      clearInterval(stackInstance.fileListBroadcastInterval);
      stackInstance.fileListBroadcastInterval = null;
    }

    // Stop subscriber check timer
    if (stackInstance.subscriberCheckTimer !== null) {
      clearTimeout(stackInstance.subscriberCheckTimer);
      stackInstance.subscriberCheckTimer = null;
    }

    // Stop mesh heartbeat interval and release ownership
    if (stackInstance.meshHeartbeatInterval !== null) {
      clearInterval(stackInstance.meshHeartbeatInterval);
      stackInstance.meshHeartbeatInterval = null;
      this.log.info(`   💓 Stopped mesh heartbeat for six '${stackInstance.sixId}'`);
    }
    if (heartbeatOwnerSixId === stackInstance.sixId) {
      heartbeatOwnerSixId = null;
      this.log.info(`   💓 Released heartbeat ownership from six '${stackInstance.sixId}'`);
    }

    // Cleanup node metadata timers and intervals
    if (stackInstance.nodeMetadata !== null) {
      this.log.info(`   🧹 Clearing node metadata timers for six '${stackInstance.sixId}'...`);

      // Clear monitoring timers
      if (stackInstance.nodeMetadata.timers.updateInterval !== null) {
        clearInterval(stackInstance.nodeMetadata.timers.updateInterval);
        stackInstance.nodeMetadata.timers.updateInterval = null;
      }

      if (stackInstance.nodeMetadata.timers.healthCheckInterval !== null) {
        clearInterval(stackInstance.nodeMetadata.timers.healthCheckInterval);
        stackInstance.nodeMetadata.timers.healthCheckInterval = null;
      }

      if (stackInstance.nodeMetadata.timers.reconnectInterval !== null) {
        clearInterval(stackInstance.nodeMetadata.timers.reconnectInterval);
        stackInstance.nodeMetadata.timers.reconnectInterval = null;
      }

      if (stackInstance.nodeMetadata.timers.topicListBroadcastInterval !== null) {
        clearInterval(stackInstance.nodeMetadata.timers.topicListBroadcastInterval);
        stackInstance.nodeMetadata.timers.topicListBroadcastInterval = null;
      }

      const sixIdTimer = stackInstance.nodeMetadata.timers.sixIdAnnounceTimer;
      if (sixIdTimer !== null) {
        clearTimeout(sixIdTimer);
        stackInstance.nodeMetadata.timers.sixIdAnnounceTimer = null;
      }

      // Clear peer tracking timers
      stackInstance.nodeMetadata.peerTracking.connectionTimers.forEach((timer, peerId) => {
        clearTimeout(timer);
        this.log.debug(`   Cleared connection timer for peer: ${peerId.substring(0, 20)}...`);
      });
      stackInstance.nodeMetadata.peerTracking.connectionTimers.clear();

      stackInstance.nodeMetadata.peerTracking.reconnectionIntervals.forEach((interval, peerId) => {
        clearInterval(interval);
        this.log.debug(`   Cleared reconnection interval for peer: ${peerId.substring(0, 20)}...`);
      });
      stackInstance.nodeMetadata.peerTracking.reconnectionIntervals.clear();

      // Clear tracking data
      stackInstance.nodeMetadata.peerTracking.connectionAttempts.clear();
      stackInstance.nodeMetadata.peerTracking.lastConnectionAttempt.clear();
      stackInstance.nodeMetadata.peerTracking.blockedPeers.clear();

      this.log.info(`   ✅ Cleared all timers and tracking data for six '${stackInstance.sixId}'`);
    }

    // Cleanup subscriptions
    if (stackInstance.subscriptions !== null) {
      stackInstance.subscriptions.cleanup();
      stackInstance.subscriptions = null;
    }

    // Shutdown Helia node first (depends on LibP2P)
    if (stackInstance.heliaNode !== null) {
      try {
        this.log.info(`   🔄 Stopping Helia node for six '${stackInstance.sixId}'...`);
        await this.heliaService.destroyNode(stackInstance.heliaNode);
        stackInstance.heliaNode = null;
        this.log.info(`   ✅ Helia node stopped for six '${stackInstance.sixId}'`);
      } catch (error) {
        this.log.error(
          `Error stopping Helia node for six '${stackInstance.sixId}':`,
          error
        );
      }
    }

    // Shutdown LibP2P node last using proper cleanup method
    if (stackInstance.libp2pNode !== null) {
      try {
        this.log.info(`   🔄 Stopping LibP2P node for six '${stackInstance.sixId}'...`);
        await this.libp2pService.stopNode(stackInstance.libp2pNode);
        stackInstance.libp2pNode = null;
        this.log.info(`   ✅ LibP2P node stopped for six '${stackInstance.sixId}'`);
      } catch (error) {
        this.log.error(
          `Error stopping LibP2P node for six '${stackInstance.sixId}':`,
          error
        );
      }
    }

    // Clear nodeMetadata reference
    stackInstance.nodeMetadata = null;

    this.log.info(`✅ Stack cleanup complete for six '${stackInstance.sixId}'`);
  }
}
